import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import { addComposeLinkViewState, createSharingLinkView } from 'owa-compose-link';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    addSharingLinkToContianer,
    AnchorElementsSource,
    getLinkIdFromAnchorElementId,
    getSharingLinkInfo,
    SharingLinkInfo,
} from 'owa-link-data';
import { isStringNullOrWhiteSpace } from 'owa-localize';
import { lazyTryCheckPermForOneLink } from 'owa-recipient-permission-checker';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import { GetSharingInfoResponseBase } from 'owa-fileprovider-link-services';
import { trace } from 'owa-trace';
import { onSharingLinkDetected } from '../../actions/internalActions';
import { getSharingInformation } from '../../orchestrators/getSharingInformation';
import LinkPlugin from '../../plugin/LinkPlugin';
import { LinkIdContainer } from '../../schema/LinkIdContainer';
import { addBeautifiedPrefixToId } from '../beautifiedPrefix';
import { isPureAnchorElement } from '../processAnchorElementsFromHtmlContent';
import { displaySpinnerWhenShouldBeautifyLink } from './displaySpinnerWhenShouldBeautifyLink';
import { getSharingLinkInfoAndVerifyHref } from './getSharingLinkInfoAndVerifyHref';
import { logUsageAfterGetSharingInfo } from './logUsageAfterGetSharingInfo';
import { prefetchGetWACInfo } from './prefetchGetWACInfo';
import { removeSpinnerWhenShouldBeautifyLink } from './removeSpinnerWhenShouldBeautifyLink';
import { stopReasonAfterGetSharingInfo } from './stopReasonAfterGetSharingInfo';
import { updateSharePointLinkPermissionInDraft } from './updateSharePointLinkPermissionInDraft';

// In some cases there are multiple editor events that detect the same link at about the same time.
// We keep track of links that we are fetching sharing info for, to prevent duplicate calls for the
// same link.
const pendingLinkIds = [];
export async function getSharingInfoToTryUpdateLink(
    linkPlugin: LinkPlugin,
    providerType: AttachmentDataProviderType,
    shouldBeautifyLink: boolean, // We would display a spinner during server call, change the text, add file icon, display error icon & red text for links with sharing issues, context menu on click
    shouldRehydrate: boolean, // We won't change the link, but display error icon & red text for links with sharing issues, context menu on click
    isPreviouslyBeautified: boolean,
    anchorElement: HTMLAnchorElement,
    linkIdContainer: LinkIdContainer,
    linkIndex: number, // The link index when there are a collection of anchor elements; otherwise, just put 0
    linkIdsWithSpinner: string[],
    anchorElementsSource: AnchorElementsSource
): Promise<void> {
    if (!linkPlugin.editor) {
        return;
    }

    trace.info(
        `LinkPlugin.getSharingInfoToTryUpdateLink: shouldBeautify=${shouldBeautifyLink} isPreviouslyBeautified=${isPreviouslyBeautified}, anchorElementsSource=${anchorElementsSource}`
    );

    const datapoint: PerformanceDatapoint = new PerformanceDatapoint('BeautifyLinkE2E');
    datapoint.addCustomData({ anchorElementsSource: anchorElementsSource });

    const addedElements: Node[] | null = displaySpinnerWhenShouldBeautifyLink(
        shouldBeautifyLink,
        linkPlugin,
        linkIdsWithSpinner,
        anchorElement
    );
    const originalAnchorElementId = anchorElement.id;
    let linkId: string = getLinkIdFromAnchorElementId(anchorElement.id);

    let sharingLink: SharingLinkInfo = await getSharingLinkInfoAndVerifyHref(anchorElement);
    let response: GetSharingInfoResponseBase;
    let isLinkPastedFromOfficeClient: boolean = false;
    // After user leaves the compose and comes back, the plugin has been disposed and we need to rerender the UI
    // But we don't need to perform the following tasks
    // 1. send the server call for links we already have the getSharingInformation response in the model store(sharingLink will not be null)
    // 2. we never pass true for shouldBeautifyLink, so we don't need to remove Spinner nor add prefix to id.
    if (!sharingLink) {
        if (pendingLinkIds.indexOf(linkId) !== -1) {
            return;
        }

        pendingLinkIds.push(linkId);
        logUsage('AttachmentComposeSPLinks', { anchorElementSource: anchorElementsSource });
        datapoint.addCheckmark('GetSharingInfoStart');

        response = await getSharingInformation(
            anchorElement.href,
            anchorElementsSource,
            shouldBeautifyLink,
            shouldRehydrate,
            providerType
        );
        datapoint.addCheckmark('GetSharingInfoEnd');
        const index = pendingLinkIds.indexOf(linkId);
        if (index > 0) {
            pendingLinkIds.splice(index, 1);
        }

        removeSpinnerWhenShouldBeautifyLink(
            shouldBeautifyLink,
            linkIdsWithSpinner,
            anchorElement,
            addedElements
        );
        const stopReason: string = stopReasonAfterGetSharingInfo(
            linkPlugin,
            originalAnchorElementId,
            linkIndex,
            anchorElement
        );
        if (stopReason) {
            datapoint.addCustomData({ canceledReason: stopReason });
            datapoint.end();
            return;
        }

        // Undo snapshot must be created before the id is prefixed with the special beautified token
        linkPlugin.editor.addUndoSnapshot();

        const isValidResponse: boolean =
            response && response.ResultCode === AttachmentResultCode.Success;
        const hasValidFileName: boolean = !isStringNullOrWhiteSpace(response.FileName);

        // Office client copy link content is a pure anchor element with file name as the text.
        // We have to wait for getSharingInformation response to determine this scenario.
        isLinkPastedFromOfficeClient =
            isFeatureEnabled('doc-link-shareCompose') &&
            isPureAnchorElement(anchorElement) &&
            anchorElementsSource === AnchorElementsSource.PasteHtml &&
            isValidResponse &&
            anchorElement.text === response.FileName;

        if (
            (shouldBeautifyLink || isLinkPastedFromOfficeClient) &&
            isValidResponse &&
            hasValidFileName
        ) {
            addBeautifiedPrefixToId(anchorElement);
        }
        logUsageAfterGetSharingInfo(
            shouldBeautifyLink,
            isValidResponse,
            hasValidFileName,
            shouldRehydrate
        );
    } else if (shouldBeautifyLink) {
        trace.error(
            'We should not pass true for shouldBeautifyLink when processing the same link again.'
        );
    }

    // The anchorElement id may have changed above
    linkId = getLinkIdFromAnchorElementId(anchorElement.id);

    // If sharingLink already exists, we don't need to add it to model store nor update the sharing tips
    // we just need to create ComposeLinkViewState.
    if (!sharingLink) {
        onSharingLinkDetected(
            linkId,
            anchorElement.href,
            linkIdContainer.sharingLinkIds,
            response,
            shouldBeautifyLink || shouldRehydrate,
            isPreviouslyBeautified,
            anchorElementsSource,
            providerType
        );
    } else if (isPreviouslyBeautified || sharingLink.sharingInfo.getSharingInfoSucceeded) {
        addComposeLinkViewState(linkId);
        addSharingLinkToContianer(anchorElement.id, linkIdContainer.sharingLinkIds);
    }

    // Always try to check recipient permission
    const tryCheckPermForOneLink = await lazyTryCheckPermForOneLink.import();
    tryCheckPermForOneLink(
        linkId,
        linkPlugin.getRecipientWells(),
        linkPlugin.fromAddress,
        linkIdContainer.composeId
    );

    // No other UI changes, just leave
    if (!shouldBeautifyLink && !shouldRehydrate) {
        datapoint.addCustomData({
            didBeautifyOrRehydrate: false,
            shouldBeautify: false,
            shouldRehydrate: false,
        });
        datapoint.end();
        return;
    }

    // Retrieve sharingLink if we haven't got one.
    if (!sharingLink) {
        sharingLink = getSharingLinkInfo(linkId);
    }

    const isLinkWithValidFileName: boolean =
        sharingLink && !isStringNullOrWhiteSpace(sharingLink.fileName);
    const isValidSharingInfo: boolean =
        sharingLink && sharingLink.sharingInfo.getSharingInfoSucceeded;

    // Beautify or rehydrate the link when conditions are met.
    if (isValidSharingInfo || isPreviouslyBeautified) {
        updateSharePointLinkPermissionInDraft(sharingLink, originalAnchorElementId);

        createSharingLinkView(
            linkPlugin.editor,
            linkPlugin.getRecipientWells,
            linkPlugin.fromAddress,
            linkIdContainer.composeId,
            anchorElement,
            sharingLink,
            isLinkWithValidFileName,
            sharingLink.mimeType,
            shouldBeautifyLink || isLinkPastedFromOfficeClient,
            isPreviouslyBeautified,
            false /* insertFromFilePicker */
        );
        datapoint.addCustomData({
            didBeautifyOrRehydrate: true,
            shouldBeautify: shouldBeautifyLink,
            shouldRehydrate: shouldRehydrate,
        });

        await prefetchGetWACInfo(anchorElement.href);
    } else {
        datapoint.addCustomData({
            didBeautifyOrRehydrate: false,
            shouldBeautify: shouldBeautifyLink,
            shouldRehydrate: shouldRehydrate,
        });
    }

    datapoint.end();
}
